import { createBrowserRouter, Outlet } from 'react-router'
import { type ReactNode, lazy, Suspense, useEffect, useState } from 'react'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary'
import { AppShell } from '@/components/layout/AppShell'
import { NavigationProgress } from '@/components/NavigationProgress'
import { useAuthStore } from '@/stores/auth'
import { ROUTES } from '@/constants/routes'
import { startRouteProgress, stopRouteProgress } from '@/lib/routeProgress'
// Eager: the two entry views every user hits first. Everything else is lazy
// so its code rides a route chunk (R42) — and i18n namespaces can later ride
// the same chunks.
import LandingView from '@/views/LandingView'
import HomeView from '@/views/HomeView'
// Eager: tiny embed gate (lazy tool views still suspend under Sus below).
import OrgStaffToolGate from '@/views/OrgStaffToolGate'

import { routeImporters } from './importers'

const ProfilesView = lazy(routeImporters[ROUTES.PROFILES])
const ProfileDetailView = lazy(routeImporters[ROUTES.PROFILE_DETAIL])
const SOSView = lazy(routeImporters[ROUTES.SOS])
const LocationView = lazy(routeImporters[ROUTES.LOCATION])
const SessionsView = lazy(routeImporters[ROUTES.SESSIONS])
const CommunityView = lazy(routeImporters[ROUTES.COMMUNITY])
const FindHelpView = lazy(routeImporters[ROUTES.FIND_HELP])
const MessagingLayout = lazy(routeImporters[ROUTES.MESSAGING])
const MessagingView = lazy(() => import('@/views/MessagingView'))
const MessagingThreadView = lazy(() => import('@/views/MessagingThreadView'))
const SettingsView = lazy(routeImporters[ROUTES.SETTINGS])
// Legacy /org/* staff console → Lumen (OrgConsoleRedirect). SOS/location/camera
// keep React views only when Filament embeds them (?embed=1 / iframe).
const OrgConsoleRedirect = lazy(() => import('@/views/OrgConsoleRedirect'))
const CommandCenterView = lazy(routeImporters[ROUTES.COMMAND_CENTER])
const CommandCenterUsersView = lazy(() => import('@/views/CommandCenterUsersView'))
const CommandCenterUserDetailView = lazy(() => import('@/views/CommandCenterUserDetailView'))
const CommandCenterOrgsView = lazy(() => import('@/views/CommandCenterOrgsView'))
const CommandCenterEmailsView = lazy(() => import('@/views/CommandCenterEmailsView'))
const CommandCenterSOSView = lazy(() => import('@/views/CommandCenterSOSView'))
const CommandCenterPanicView = lazy(() => import('@/views/CommandCenterPanicView'))
const CommandCenterResourcesView = lazy(() => import('@/views/CommandCenterResourcesView'))
const CommandCenterIDsView = lazy(() => import('@/views/CommandCenterIDsView'))
const CommandCenterMaintenanceView = lazy(() => import('@/views/CommandCenterMaintenanceView'))
const MissionControlView = lazy(routeImporters[ROUTES.MISSION_CONTROL])
const FeedbackView = lazy(routeImporters[ROUTES.FEEDBACK])
const DependentSafetyView = lazy(() => import('@/views/DependentSafetyView'))
const InviteAcceptView = lazy(() => import('@/views/InviteAcceptView'))
const OrgInviteAcceptView = lazy(() => import('@/views/OrgInviteAcceptView'))
const QRView = lazy(routeImporters[ROUTES.QR])
const OrgSOSView = lazy(() => import('@/views/OrgSOSView'))
const OrgLocationView = lazy(() => import('@/views/OrgLocationView'))
const OrgCameraView = lazy(() => import('@/views/OrgCameraView'))
const AACBoardView = lazy(() => import('@/views/AACBoardView'))
const PrivacyPolicyView = lazy(() => import('@/views/PrivacyPolicyView'))
const SecurityPrivacyView = lazy(() => import('@/views/SecurityPrivacyView'))
const TermsView = lazy(() => import('@/views/TermsView'))
const AboutView = lazy(() => import('@/views/AboutView'))
const PanicWatchView = lazy(() => import('@/views/PanicWatchView'))
const FunderView = lazy(() => import('@/views/FunderView'))
const PassportView = lazy(() => import('@/views/PassportView'))
const HazardsView = lazy(routeImporters[ROUTES.HAZARDS])
const WeatherView = lazy(routeImporters[ROUTES.WEATHER])
const VerifyEmailView = lazy(() => import('@/views/VerifyEmailView'))
const AuthActionView = lazy(() => import('@/views/AuthActionView'))
const OnboardingView = lazy(() => import('@/views/OnboardingView'))
const RegisterView = lazy(() => import('@/views/RegisterView'))
const LogoutView = lazy(() => import('@/views/LogoutView'))

/**
 * Lazy-route fallback. Was bg-slate-950 + “Loading…” which flashed a black
 * screen on every slow chunk (felt random and harsh). Light shell + delay so
 * sub-~180ms loads paint nothing at all.
 */
function RouteChunkFallback() {
  // Drive top blue progress bar while the lazy chunk loads.
  useEffect(() => {
    startRouteProgress()
    return () => stopRouteProgress()
  }, [])

  const [show, setShow] = useState(false)
  useEffect(() => {
    const t = window.setTimeout(() => setShow(true), 180)
    return () => window.clearTimeout(t)
  }, [])
  // Match AppShell main (slate-100) so the shell doesn't "flash black".
  // Top NavigationProgress is the primary loading cue (like Lumen).
  if (!show) {
    return <div className="flex-1 min-h-[40vh] bg-slate-100" aria-hidden />
  }
  return (
    <div
      className="flex flex-1 min-h-[40vh] items-center justify-center bg-slate-100 text-slate-400 text-sm"
      role="status"
      aria-live="polite"
    >
      <span className="sr-only">Loading…</span>
    </div>
  )
}

function Sus({ children }: { children: ReactNode }) {
  return <Suspense fallback={<RouteChunkFallback />}>{children}</Suspense>
}

/** Root shell: top progress bar on every route. */
function RootLayout() {
  return (
    <>
      <NavigationProgress />
      <Outlet />
    </>
  )
}

function PublicInfoRoute({ children }: { children: ReactNode }) {
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn())

  if (!isLoggedIn) return (
    <div className="h-[100dvh] overflow-y-auto bg-slate-100">
      {children}
    </div>
  )
  return <AppShell>{children}</AppShell>
}

export const router = createBrowserRouter([
  {
  element: <RootLayout />,
  errorElement: <RouteErrorBoundary />,
  children: [
  {
    path: ROUTES.LANDING,
    element: (
      <ProtectedRoute guestOnly>
        <LandingView />
      </ProtectedRoute>
    ),
  },
  {
    // Public recovery: no auth shell. Clears Firebase session even when stuck.
    path: ROUTES.LOGOUT,
    element: (
      <Sus>
        <LogoutView />
      </Sus>
    ),
  },
  {
    path: ROUTES.HOME,
    element: <ProtectedRoute><HomeView /></ProtectedRoute>,
  },
  {
    path: ROUTES.PROFILES,
    element: <ProtectedRoute requireFamilyRole><Sus><ProfilesView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.PROFILE_DETAIL,
    element: <ProtectedRoute requireFamilyRole><Sus><ProfileDetailView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.SOS,
    element: <ProtectedRoute requireFamilyRole><Sus><SOSView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.LOCATION,
    element: <ProtectedRoute requireFamilyRole><Sus><LocationView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.SESSIONS,
    element: <ProtectedRoute requireFamilyRole><Sus><SessionsView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.COMMUNITY,
    element: <ProtectedRoute><Sus><CommunityView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.FIND_HELP,
    element: <ProtectedRoute><Sus><FindHelpView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.MESSAGING,
    element: <ProtectedRoute><Sus><MessagingLayout /></Sus></ProtectedRoute>,
    children: [
      { index: true, element: <Sus><MessagingView /></Sus> },
      { path: ':conversationId', element: <Sus><MessagingThreadView /></Sus> },
    ],
  },
  {
    path: ROUTES.SETTINGS,
    element: <ProtectedRoute><Sus><SettingsView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.ORG,
    element: <ProtectedRoute requireOrgRole><Sus><OrgConsoleRedirect /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.ORG_TEAM,
    element: <ProtectedRoute requireOrgRole><Sus><OrgConsoleRedirect /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.COMMAND_CENTER,
    element: <ProtectedRoute hideHeader hideFab><Sus><CommandCenterView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.COMMAND_CENTER_USERS,
    element: <ProtectedRoute hideHeader hideFab><Sus><CommandCenterUsersView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.COMMAND_CENTER_USER_DETAIL,
    element: <ProtectedRoute hideHeader hideFab><Sus><CommandCenterUserDetailView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.COMMAND_CENTER_ORGS,
    element: <ProtectedRoute hideHeader hideFab><Sus><CommandCenterOrgsView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.COMMAND_CENTER_EMAILS,
    element: <ProtectedRoute hideHeader hideFab><Sus><CommandCenterEmailsView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.COMMAND_CENTER_SOS,
    element: <ProtectedRoute hideHeader hideFab><Sus><CommandCenterSOSView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.COMMAND_CENTER_PANIC,
    element: <ProtectedRoute hideHeader hideFab><Sus><CommandCenterPanicView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.COMMAND_CENTER_RESOURCES,
    element: <ProtectedRoute hideHeader hideFab><Sus><CommandCenterResourcesView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.COMMAND_CENTER_IDS,
    element: <ProtectedRoute hideHeader hideFab><Sus><CommandCenterIDsView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.COMMAND_CENTER_MAINTENANCE,
    element: <ProtectedRoute hideHeader hideFab><Sus><CommandCenterMaintenanceView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.MISSION_CONTROL,
    element: <ProtectedRoute><Sus><MissionControlView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.PANIC_WATCH,
    element: <ProtectedRoute noShell><Sus><PanicWatchView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.FEEDBACK,
    element: <ProtectedRoute><Sus><FeedbackView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.DEPENDENT_SAFETY,
    element: <ProtectedRoute noShell><Sus><DependentSafetyView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.PASSPORT,
    element: <Sus><PassportView /></Sus>,
  },
  {
    path: ROUTES.INVITE_ACCEPT,
    element: <Sus><InviteAcceptView /></Sus>,
  },
  {
    path: ROUTES.ORG_INVITE_ACCEPT,
    element: <Sus><OrgInviteAcceptView /></Sus>,
  },
  {
    path: ROUTES.ORG_APPLY,
    // New org applications live on Lumen register (not React OrgApplyView).
    element: <ProtectedRoute><Sus><OrgConsoleRedirect /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.QR,
    element: <ProtectedRoute><Sus><QRView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.ORG_ENROLLED,
    element: <ProtectedRoute requireOrgRole><Sus><OrgConsoleRedirect /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.ORG_NOTES,
    element: <ProtectedRoute requireOrgRole><Sus><OrgConsoleRedirect /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.ORG_SOS,
    element: (
      <ProtectedRoute requireOrgRole>
        <OrgStaffToolGate>
          <Sus>
            <OrgSOSView />
          </Sus>
        </OrgStaffToolGate>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.ORG_LOCATION,
    element: (
      <ProtectedRoute requireOrgRole>
        <OrgStaffToolGate>
          <Sus>
            <OrgLocationView />
          </Sus>
        </OrgStaffToolGate>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.ORG_CAMERA,
    element: (
      <ProtectedRoute requireOrgRole>
        <OrgStaffToolGate>
          <Sus>
            <OrgCameraView />
          </Sus>
        </OrgStaffToolGate>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.ORG_PENDING,
    element: <ProtectedRoute requireOrgRole><Sus><OrgConsoleRedirect /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.ORG_SETTINGS,
    element: <ProtectedRoute requireOrgRole><Sus><OrgConsoleRedirect /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.AAC,
    element: <ProtectedRoute noShell><Sus><AACBoardView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.PRIVACY_POLICY,
    element: <PublicInfoRoute><Sus><PrivacyPolicyView /></Sus></PublicInfoRoute>,
  },
  {
    path: ROUTES.PRIVACY_POLICY_LEGACY,
    element: <PublicInfoRoute><Sus><PrivacyPolicyView /></Sus></PublicInfoRoute>,
  },
  {
    path: ROUTES.TERMS,
    element: <PublicInfoRoute><Sus><TermsView /></Sus></PublicInfoRoute>,
  },
  {
    path: ROUTES.SECURITY_PRIVACY,
    element: <PublicInfoRoute><Sus><SecurityPrivacyView /></Sus></PublicInfoRoute>,
  },
  {
    path: ROUTES.ABOUT,
    element: <PublicInfoRoute><Sus><AboutView /></Sus></PublicInfoRoute>,
  },
  {
    path: ROUTES.FUNDER,
    element: <ProtectedRoute noShell><Sus><FunderView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.HAZARDS,
    element: <ProtectedRoute><Sus><HazardsView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.WEATHER,
    element: <ProtectedRoute><Sus><WeatherView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.VERIFY_EMAIL,
    element: <Sus><VerifyEmailView /></Sus>,
  },
  {
    path: ROUTES.AUTH_ACTION,
    element: <Sus><AuthActionView /></Sus>,
  },
  {
    path: ROUTES.REGISTER,
    element: <ProtectedRoute guestOnly noShell><Sus><RegisterView /></Sus></ProtectedRoute>,
  },
  {
    path: ROUTES.ONBOARDING,
    element: <ProtectedRoute requireFamilyRole noShell><Sus><OnboardingView /></Sus></ProtectedRoute>,
  },
  {
    // Unknown /org/... paths → Lumen (same redirect helper as known staff routes).
    path: '/org/*',
    element: <ProtectedRoute requireOrgRole><Sus><OrgConsoleRedirect /></Sus></ProtectedRoute>,
  },
  {
    path: '*',
    element: (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <p className="text-slate-500">Page not found</p>
      </div>
    ),
  },
  ],
  },
])


